/*
 * Pep Product Carousel (custom, used by sections/pep-product-carousel.liquid)
 * - <pep-product-carousel>: desktop arrows for the scroll-snap track; closes open cards on outside tap
 * - <pep-product-card>: variant / purchase option / frequency handling; add to cart
 *   is handed to Broadcast via the 'theme:cart:add' event (same as the theme's product form),
 *   so the cart drawer, loading state and errors behave like the rest of the site.
 *
 * Touch devices (no hover): the first tap on "Add to cart" opens the options, the next tap adds.
 */
(function () {
  const canHover = window.matchMedia('(hover: hover)');

  if (!customElements.get('pep-product-carousel')) {
    customElements.define(
      'pep-product-carousel',
      class PepProductCarousel extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-pep-track]');
          this.arrows = this.querySelector('[data-pep-arrows]');
          this.onOutsideClick = this.onOutsideClick.bind(this);
          document.addEventListener('click', this.onOutsideClick);

          if (!this.track || !this.arrows) return;

          this.prev = this.arrows.querySelector('[data-pep-prev]');
          this.next = this.arrows.querySelector('[data-pep-next]');
          this.update = this.update.bind(this);

          this.prev.addEventListener('click', () => this.scrollByCard(-1));
          this.next.addEventListener('click', () => this.scrollByCard(1));
          this.track.addEventListener('scroll', this.update, {passive: true});
          this.resizeObserver = new ResizeObserver(this.update);
          this.resizeObserver.observe(this.track);
          this.update();
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onOutsideClick);
          this.resizeObserver?.disconnect();
        }

        onOutsideClick(event) {
          this.querySelectorAll('pep-product-card.is-open').forEach((card) => {
            if (!card.contains(event.target)) card.classList.remove('is-open');
          });
        }

        scrollByCard(direction) {
          const slide = this.track.firstElementChild;
          if (!slide) return;
          const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          this.track.scrollBy({left: direction * (slide.offsetWidth + gap), behavior: 'smooth'});
        }

        update() {
          const maxScroll = this.track.scrollWidth - this.track.clientWidth;
          this.arrows.hidden = maxScroll <= 1;
          this.prev.disabled = this.track.scrollLeft <= 1;
          this.next.disabled = this.track.scrollLeft >= maxScroll - 1;
        }
      }
    );
  }

  if (!customElements.get('pep-product-card')) {
    customElements.define(
      'pep-product-card',
      class PepProductCard extends HTMLElement {
        connectedCallback() {
          const json = this.querySelector('[data-pep-card-json]');
          if (!json) return;
          this.data = JSON.parse(json.textContent);

          this.form = this.querySelector('[data-pep-form]');
          this.variantInput = this.querySelector('[data-pep-variant-input]');
          this.planSelect = this.querySelector('[data-pep-plan]');
          this.frequency = this.querySelector('[data-pep-frequency]');
          this.addButton = this.querySelector('[data-pep-add]');

          this.setupVideos();
          this.addEventListener('change', this.onChange.bind(this));
          this.form?.addEventListener('submit', this.onSubmit.bind(this));
          this.updatePrices();
        }

        disconnectedCallback() {
          this.videoObserver?.disconnect();
        }

        // Videos load and play only while the card is on screen; the hover video only while hovered.
        setupVideos() {
          this.videos = [...this.querySelectorAll('video[data-pep-video]')];
          // Reduced motion: keep the still (poster) frame.
          if (!this.videos.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

          this.inView = false;
          this.hovered = false;
          this.addEventListener('mouseenter', () => { this.hovered = true; this.syncVideos(); });
          this.addEventListener('mouseleave', () => { this.hovered = false; this.syncVideos(); });

          this.videoObserver = new IntersectionObserver(([entry]) => {
            this.inView = entry.isIntersecting;
            this.syncVideos();
          }, {threshold: 0.5});
          this.videoObserver.observe(this);
        }

        syncVideos() {
          this.videos?.forEach((video) => {
            const isHover = video.dataset.pepVideo === 'hover';
            const visible = isHover
              ? canHover.matches && this.hovered && !this.classList.contains('is-variant-picked')
              : !video.closest('[hidden]');
            const play = this.inView && visible;

            if (play && video.paused) {
              video.preload = 'auto';
              video.play().catch(() => {});
            } else if (!play && !video.paused) {
              video.pause();
            }
          });
        }

        get variant() {
          const id = Number(this.variantInput.value);
          return this.data.variants.find((variant) => variant.id === id);
        }

        get isSubscribe() {
          if (this.dataset.mode === 'subscription_only') return true;
          const checked = this.querySelector('[data-pep-purchase]:checked');
          return checked ? checked.value === 'subscribe' : false;
        }

        onChange(event) {
          const target = event.target;

          if (target.matches('[data-pep-variant]')) {
            this.variantInput.value = target.value;
            this.classList.add('is-variant-picked');
            this.updateMedia();
          }

          if (target.matches('[data-pep-purchase]') && this.planSelect) {
            this.planSelect.disabled = !this.isSubscribe;
            this.frequency.hidden = !this.isSubscribe;
          }

          this.updatePrices();
          this.updateButton();
        }

        onSubmit(event) {
          // Touch: first tap reveals the options instead of adding straight away.
          if (!canHover.matches && !this.classList.contains('is-open')) {
            event.preventDefault();
            this.classList.add('is-open');
            return;
          }

          // Without the theme cart (e.g. theme JS failed), fall back to a normal form post.
          if (!window.theme || !window.theme.routes) return;
          event.preventDefault();

          document.dispatchEvent(
            new CustomEvent('theme:cart:add', {
              detail: {button: this.addButton},
              bubbles: false,
            })
          );
        }

        updateMedia() {
          const variant = this.variant;
          if (!variant || !variant.mediaId) return;

          const wraps = [...this.querySelectorAll('[data-media-id]')];
          if (!wraps.some((wrap) => Number(wrap.dataset.mediaId) === variant.mediaId)) return;

          wraps.forEach((wrap) => {
            wrap.hidden = Number(wrap.dataset.mediaId) !== variant.mediaId;
          });
          this.syncVideos();
        }

        // Updates prices inside the options panel only; the card's title price stays as rendered.
        updatePrices() {
          const variant = this.variant;
          if (!variant) return;

          const plan = this.planSelect ? variant.plans[this.planSelect.value] : null;

          const oneTimePrice = this.querySelector('[data-pep-onetime-price]');
          if (oneTimePrice) oneTimePrice.textContent = variant.price;

          const subPrice = this.querySelector('[data-pep-sub-price]');
          const subCompare = this.querySelector('[data-pep-sub-compare]');
          if (subPrice && plan) {
            subPrice.textContent = plan.price;
            if (subCompare) {
              subCompare.textContent = variant.price;
              subCompare.hidden = plan.priceCents >= variant.priceCents;
            }
          }

          // Bundled subscription: "+ price / N mo" for the renewal product (see block setting).
          const recurring = this.querySelector('[data-pep-recurring]');
          if (recurring) {
            recurring.textContent = plan?.recurring || '';
            recurring.hidden = !recurring.textContent;
          }
        }

        updateButton() {
          const variant = this.variant;
          if (!variant || !this.addButton) return;

          this.addButton.disabled = !variant.available;
          const text = this.addButton.querySelector('[data-pep-add-text]');
          if (text) text.textContent = variant.available ? this.data.strings.add : this.data.strings.soldOut;
        }
      }
    );
  }
})();
